
import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Button } from "@/components/ui/button";
import LocationHero from '@/components/locations/LocationHero';
import LocationFeatures from '@/components/locations/LocationFeatures';
import LocationTestimonials from '@/components/locations/LocationTestimonials';
import LocationCTA from '@/components/locations/LocationCTA';
import { Helmet } from 'react-helmet-async';

const DyssegardLocation = () => {
  const testimonials = [
    {
      name: "Louise Madsen",
      location: "Dyssegård",
      text: "Oscars undervisning har været en fantastisk oplevelse for mig. Hans evne til at forklare musikteori på en let forståelig måde har virkelig hjulpet mig til at udvikle mig som pianist.",
      stars: 5
    },
    {
      name: "Peter Nielsen",
      location: "Dyssegård",
      text: "Min søn på 10 år har gået til klaver hos Oscar i snart et år, og udviklingen har været imponerende. Oscar formår at holde ham motiveret og interesseret, hvilket er guld værd.",
      stars: 5
    },
    {
      name: "Anja Christensen",
      location: "Dyssegård",
      text: "Jeg startede som voksen begynder uden nogen musikalsk baggrund. Oscars tålmodige undervisning og opmuntring har givet mig selvtillid ved klaveret og en ny hobby, jeg virkelig nyder.",
      stars: 5
    }
  ];

  const features = [
    {
      title: "Nem adgang fra Dyssegård",
      description: "Undervisningslokalet er let at nå fra Dyssegård med både bil og offentlig transport.",
      icon: "map-pin"
    },
    {
      title: "Fleksible tider",
      description: "Undervisningstider der kan tilpasses din hverdag og rutiner.",
      icon: "clock"
    },
    {
      title: "For alle aldre",
      description: "Børn, unge og voksne fra Dyssegård er velkomne til undervisning på alle niveauer.",
      icon: "users"
    },
    {
      title: "Konservatorieuddannet",
      description: "Undervisning af høj kvalitet fra Oscar Gilbert, som er uddannet på Rytmisk Musikkonservatorium.",
      icon: "award"
    }
  ];

  return (
    <Layout>
      <Helmet>
        <title>Klaverundervisning i Dyssegård | Få en gratis prøvetime!</title>
        <meta name="description" content="Professionel klaverundervisning i Dyssegård med Oscar Gilbert. Book en gratis prøvetime og start din musikalske rejse i dag!" />
      </Helmet>
      
      <LocationHero 
        title="Klaverundervisning i Dyssegård"
        description="Få professionel klaverundervisning med Oscar Gilbert for alle i Dyssegård-området. Undervisning foregår i indbydende omgivelser på Niels Finsens Alle 86 i Søborg, tæt på Dyssegård."
      />
      
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6">
                Klaverundervisning i <span className="text-piano-gold">Dyssegård</span>
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Oscar Gilbert tilbyder kvalitetsundervisning i klaver for elever fra Dyssegård på Niels Finsens Alle 86 i Søborg. Adressen er let tilgængelig fra Dyssegård og giver en kort transporttid.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                Uanset om du er helt nybegynder eller har spillet i mange år, vil Oscars personlige tilgang sikre, at du får den rette vejledning til at nå dine musikalske mål.
              </p>
              <p className="text-lg text-gray-700 mb-8">
                Undervisningslokalet er indrettet med fokus på komfort og akustik, så du får den optimale oplevelse hver gang du kommer til time.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild className="bg-piano-gold hover:bg-piano-gold/90 text-white">
                  <Link to="/kontakt">Book gratis prøvetime</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/undervisning">Se undervisningstilbud</Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-piano-gold to-amber-400 rounded-lg blur opacity-20"></div>
              <div className="relative bg-white p-3 rounded-lg shadow-xl">
                <div className="aspect-square bg-gray-100 rounded-md overflow-hidden">
                  <img 
                    src="/lovable-uploads/891ee3d3-fc7a-4f1b-925d-4c77de27f6ae.png" 
                    alt="Klaverundervisning i Dyssegård" 
                    className="w-full h-full object-cover object-center object-[50%_35%]"
                    width="400"
                    height="400"
                    loading="eager"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <LocationFeatures features={features} />
      
      <LocationTestimonials testimonials={testimonials} locationName="Dyssegård" title="Hvad siger mine elever i Dyssegård" />
      
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-serif font-bold mb-4">Find vej til undervisningen</h2>
              <p className="text-lg text-gray-600">
                Klaverundervisningen foregår på Niels Finsens Alle 86, 2860 Søborg, let tilgængeligt fra Dyssegård.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-xl mb-10">
              <div className="overflow-hidden rounded-md">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12607.679143124358!2d12.514096630724325!3d55.734728901797794!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x465251f89eae19fd%3A0x1029a319978e54b7!2sNiels%20Finsens%20Alle%2086%2C%202860%20S%C3%B8borg!5e0!3m2!1sda!2sdk!4v1743668909093!5m2!1sda!2sdk" 
                  width="100%" 
                  height="450" 
                  style={{ border: 0 }} 
                  allowFullScreen={true} 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Lokation på kort">
                </iframe>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h3 className="text-xl font-serif font-semibold">Adresse</h3>
                  <p className="text-gray-700">
                    Niels Finsens Alle 86<br />
                    2860 Søborg
                  </p>
                  <div className="space-y-2">
                    <p className="text-gray-700"><strong>Transport fra Dyssegård:</strong></p>
                    <p className="text-gray-700">• Kort transporttid fra Dyssegård</p>
                    <p className="text-gray-700">• Gode busforbindelser</p>
                    <p className="text-gray-700">• Gratis parkering ved undervisningslokalet</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="text-xl font-serif font-semibold">Kontakt</h3>
                  <p className="text-gray-700">
                    Telefon: <a href="tel:+4540817127" className="text-piano-gold hover:underline">+45 40 81 71 27</a>
                  </p>
                  <p className="text-gray-700">
                    Email: <a href="mailto:kontakt@pianoundervisning.dk" className="text-piano-gold hover:underline">kontakt@pianoundervisning.dk</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <LocationCTA locationName="Dyssegård" />
    </Layout>
  );
};

export default DyssegardLocation;
